import React, { useEffect, useState } from 'react';
import { withTranslation } from 'react-i18next';
import { Link, Location } from '@reach/router';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { FeatureGlyph, FeatureStatusIcons } from './FeatureMarkers';
import { FeaturesHelpButton } from './FeaturesHelpGuide';
import { isPaidAddOn } from './PaidFeatureMark';
import { ActivateMark } from '../leftnav/NavLockedBubble';
import {
  displaySafeText,
  displaySolutionTitle,
  fetchEntitlements,
  getSessionContactEmail,
  isComingSoonFeature,
  isFeatureVisibleToAccount,
  recordFeatureEvent,
  submitFeatureLead,
} from '../../utils/productLicensing';
import './features.css';

const INCLUDE_HEADING = '<p><strong>What this includes</strong></p>';
const EXCLUDE_HEADING = '<p><strong>What this does not include</strong></p>';

const withSideBySideSections = (html) => {
  const body = String(html || '');
  if (!body || body.indexOf('feature-detail-sections') !== -1) {
    return body;
  }
  const includeIdx = body.indexOf(INCLUDE_HEADING);
  const excludeIdx = body.indexOf(EXCLUDE_HEADING);
  if (includeIdx === -1 || excludeIdx === -1 || excludeIdx <= includeIdx) {
    return body;
  }
  const intro = body.slice(0, includeIdx);
  const includes = body.slice(includeIdx, excludeIdx);
  const excludes = body.slice(excludeIdx);
  return (
    intro +
    '<div class="feature-detail-sections">' +
    '<div class="feature-detail-section">' +
    includes +
    '</div>' +
    '<div class="feature-detail-section">' +
    excludes +
    '</div>' +
    '</div>'
  );
};

const featureKeyFromPath = (pathname, fallback) => {
  const match = String(pathname || '').match(/\/features\/([^/?#]+)/);
  if (!match || !match[1]) {
    return fallback || '';
  }
  try {
    return decodeURIComponent(match[1]);
  } catch (_err) {
    return match[1];
  }
};

const FeatureDetailPage = ({ t, featureKey }) => {
  const [loading, setLoading] = useState(true);
  const [feature, setFeature] = useState(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [alreadyRequested, setAlreadyRequested] = useState(false);
  const [hasOpenLead, setHasOpenLead] = useState(false);
  const [formError, setFormError] = useState('');
  const [contactEmail, setContactEmail] = useState(getSessionContactEmail);
  const [notes, setNotes] = useState('');
  const accountName = displaySafeText(
    sessionStorage.getItem('parentAccountName'),
    t('Features.AccountFallback', { defaultValue: 'Current pharmacy' })
  );
  const boxId = localStorage.getItem('selectedLocation');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFeature(null);
    setError('');
    setSubmitted(false);
    setAlreadyRequested(false);
    setHasOpenLead(false);
    setFormError('');
    const load = async () => {
      try {
        const entitlementData = await fetchEntitlements({
          boxId: boxId && boxId !== 'null' ? boxId : undefined,
        });
        if (cancelled) return;
        const match = ((entitlementData && entitlementData.entitlements) || []).find(
          (item) =>
            item.feature_key === featureKey && isFeatureVisibleToAccount(item)
        );
        if (!match) {
          setError(
            t('Features.NotFound', {
              defaultValue: 'This feature is not enabled for this account.',
            })
          );
        } else {
          setFeature(match);
          setHasOpenLead(
            !!(
              match.has_open_lead ||
              (match.open_lead_location_ids &&
                match.open_lead_location_ids.length)
            )
          );
          recordFeatureEvent({
            event: 'feature_page_viewed',
            feature_key: featureKey,
            location_id: boxId && boxId !== 'null' ? boxId : undefined,
          });
        }
      } catch (_err) {
        if (!cancelled) {
          setError(
            t('Features.LoadError', {
              defaultValue: 'Unable to load features right now.',
            })
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [boxId, featureKey, t]);

  const followUpMode = hasOpenLead && !submitted;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError('');
    const email = String(contactEmail || '').trim();
    if (!email.includes('@')) {
      setFormError(
        t('Features.TalkError', {
          defaultValue: 'Unable to send this request. Please try again.',
        })
      );
      return;
    }
    setSubmitting(true);
    try {
      const result = await submitFeatureLead({
        feature_key: featureKey,
        location_id: boxId && boxId !== 'null' ? boxId : undefined,
        contact_email: email,
        notes,
      });
      const isFollowUp = !!(result && result.already_submitted);
      setAlreadyRequested(isFollowUp);
      setSubmitted(true);
      setHasOpenLead(true);
      setNotes('');
    } catch (err) {
      setFormError(
        (err && err.message) ||
          t('Features.TalkError', {
            defaultValue: 'Unable to send this request. Please try again.',
          })
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="feature-detail">
        <CircularProgress size={28} />
      </div>
    );
  }

  if (error || !feature) {
    return (
      <div className="feature-detail">
        <Link to="/home/features" className="feature-detail-crumb">
          <ArrowBack fontSize="small" />
          {t('Features.Breadcrumb', { defaultValue: 'Solutions' })}
        </Link>
        <p className="licensing-empty">{error}</p>
      </div>
    );
  }

  const geoBlocked = feature.reason === 'geo_blocked';
  const locked = !feature.enabled && !geoBlocked;
  const showTalkForm = locked && feature.talk_to_expert !== false;
  const statusClass = geoBlocked
    ? 'is-geo'
    : feature.enabled
      ? 'is-enabled'
      : 'is-locked';
  const statusLabel = geoBlocked
    ? t('Features.StatusGeo', { defaultValue: 'Not available here' })
    : feature.enabled
      ? t('Features.StatusEnabled', { defaultValue: 'Enabled' })
      : t('Features.StatusLocked', { defaultValue: 'Not enabled' });

  const talkPanel = showTalkForm ? (
    <div className="feature-talk-panel" data-tour="features-talk">
      <form className="feature-talk-form" onSubmit={handleSubmit}>
        <h3>
          {followUpMode
            ? t('Features.TalkFollowUpTitle', {
                defaultValue: 'Already requested for this account',
              })
            : t('Features.TalkTitle', { defaultValue: 'Talk to Expert' })}
        </h3>
        <p>
          {followUpMode
            ? t('Features.TalkFollowUpIntro', {
                defaultValue:
                  'This pharmacy already has an open request for this feature. Send an update if you have more context. Enablement applies to all locations on this account.',
              })
            : t('Features.TalkIntro', {
                defaultValue:
                  'Share a bit of context and an iLocal specialist will follow up. After the offline sale, enablement is for the whole pharmacy account and applies to all of its locations.',
              })}
        </p>
        <TextField
          fullWidth
          margin="normal"
          label={t('Features.Account', { defaultValue: 'Account' })}
          value={accountName}
          InputProps={{ readOnly: true }}
        />
        {submitted ? (
          <div
            className={
              alreadyRequested ? 'feature-talk-already' : 'feature-talk-success'
            }
          >
            {alreadyRequested
              ? t('Features.TalkFollowUpSuccess', {
                  defaultValue:
                    'This request is already open for this account. A follow-up was sent to iLocal. Enablement will apply to all locations on this account.',
                })
              : t('Features.TalkSuccess', {
                  defaultValue:
                    'Thanks. An iLocal specialist typically responds within one business day. This feature is enabled offline after that conversation — nothing will be charged in the portal.',
                })}
          </div>
        ) : (
          <div>
            {followUpMode ? (
              <div className="feature-talk-already">
                {t('Features.TalkAlreadyRequested', {
                  defaultValue:
                    'This feature is already requested for this account.',
                })}
              </div>
            ) : null}
            <TextField
              fullWidth
              required
              margin="normal"
              type="email"
              label={t('Features.ContactEmail', { defaultValue: 'Contact email' })}
              value={contactEmail}
              onChange={(event) => setContactEmail(event.target.value)}
            />
            <TextField
              fullWidth
              margin="normal"
              multiline
              minRows={3}
              label={t('Features.Notes', { defaultValue: 'Notes (optional)' })}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
            />
            {formError ? (
              <p className="licensing-empty" style={{ color: '#b91c1c' }}>
                {formError}
              </p>
            ) : null}
            <Button
              type="submit"
              variant="contained"
              disabled={submitting}
              sx={{
                mt: 2,
                textTransform: 'none',
                backgroundColor: '#00a7e1',
                boxShadow: '0 8px 18px rgba(0, 167, 225, 0.28)',
                borderRadius: '10px',
                fontWeight: 600,
                px: 2.5,
                '&:hover': { backgroundColor: '#0096cc' },
              }}
            >
              {submitting
                ? t('Features.TalkSending', { defaultValue: 'Sending...' })
                : followUpMode
                  ? t('Features.TalkFollowUpSubmit', {
                      defaultValue: 'Send a follow-up',
                    })
                  : t('Features.TalkSubmit', { defaultValue: 'Talk to Expert' })}
            </Button>
          </div>
        )}
      </form>
    </div>
  ) : null;

  return (
    <div className="feature-detail">
      <div className="feature-detail-crumb-row">
        <Link to="/home/features" className="feature-detail-crumb">
          <ArrowBack fontSize="small" />
          {t('Features.Breadcrumb', { defaultValue: 'Solutions' })}
        </Link>
        <FeaturesHelpButton t={t} />
      </div>

      <div className="feature-detail-hero">
        <span className={`feature-card-icon ${statusClass}`}>
          <FeatureGlyph featureKey={feature.feature_key} />
        </span>
        <div className="feature-detail-hero-copy">
          <div className="feature-detail-header">
            <h1 className="feature-detail-title">
              {displaySolutionTitle(feature.feature_key, feature.title)}
            </h1>
            <span className="feature-detail-header-marks">
              {locked ? (
                <ActivateMark
                  label={t('Solutions.Activate', { defaultValue: 'Activate' })}
                />
              ) : null}
              <FeatureStatusIcons entitlement={feature} t={t} />
            </span>
          </div>
          <div className="feature-card-meta">
            <span className={`feature-card-status ${statusClass}`}>
              {statusLabel}
            </span>
            {isComingSoonFeature(feature.feature_key, feature.title) &&
            !feature.enabled ? (
              <span className="solution-coming-soon">
                {t('Solutions.ComingSoon', { defaultValue: 'Coming soon' })}
              </span>
            ) : null}
          </div>
          {isPaidAddOn(feature) && !feature.enabled ? (
            <p className="feature-detail-paid">
              {t('Features.PaidNote', {
                defaultValue:
                  'Requires a paid subscription, speak with an expert to activate',
              })}
            </p>
          ) : null}
        </div>
      </div>

      <div className={`feature-detail-layout${showTalkForm ? ' has-aside' : ''}`}>
        <div className="feature-detail-main">
          {feature.enabled && !geoBlocked ? (
            <div className="feature-enabled-banner">
              {t('Features.EnabledBanner', {
                defaultValue:
                  'Enabled for this account. All locations can use this feature.',
              })}
              {feature.enabled_path ? (
                <div>
                  <Button
                    component={Link}
                    to={feature.enabled_path}
                    variant="contained"
                    sx={{
                      mt: 2,
                      textTransform: 'none',
                      backgroundColor: '#00a7e1',
                      '&:hover': { backgroundColor: '#0096cc' },
                    }}
                  >
                    {feature.feature_key === 'custom_brand_messaging'
                      ? t('Features.OpenMessageControl', {
                          defaultValue: 'Open Studio',
                        })
                      : feature.feature_key === 'campaigns'
                        ? t('Features.OpenCampaigns', {
                            defaultValue: 'Open Campaigns',
                          })
                      : t('Features.OpenFeature', { defaultValue: 'Open' })}
                  </Button>
                </div>
              ) : null}
            </div>
          ) : null}
          {geoBlocked ? (
            <p className="licensing-empty">
              {t('Features.GeoBlockedDetail', {
                defaultValue:
                  'This location cannot offer this feature because of local rules.',
              })}
            </p>
          ) : null}
          <div
            className="feature-detail-body"
            dangerouslySetInnerHTML={{
              __html: withSideBySideSections(feature.body),
            }}
          />
        </div>
        {talkPanel}
      </div>
    </div>
  );
};

const FeatureDetail = ({ t, featureKey }) => (
  <Location>
    {({ location }) => {
      const resolvedKey = featureKeyFromPath(location.pathname, featureKey);
      return (
        <FeatureDetailPage
          key={resolvedKey}
          t={t}
          featureKey={resolvedKey}
        />
      );
    }}
  </Location>
);

export default withTranslation()(FeatureDetail);



// WEBPACK FOOTER //
// ./src/components/features/FeatureDetail.js